import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

// Configuration
const CART_LIFESPAN_DAYS = 7; // Days

// Helper functions for user-specific cart persistence
const getCartKey = (userId) => `cart_${userId}`;
const getCartTimestampKey = (userId) => `cart_timestamp_${userId}`;

const saveCartToStorage = (userId, cartData) => {
  if (!userId) return;

  const cartKey = getCartKey(userId);
  const timestampKey = getCartTimestampKey(userId);
  const timestamp = Date.now();

  localStorage.setItem(cartKey, JSON.stringify(cartData));
  localStorage.setItem(timestampKey, timestamp.toString());
};

// Clean up expired carts for all users
const cleanupExpiredCarts = () => {
  const keys = Object.keys(localStorage);
  const cartTimestampKeys = keys.filter(key => key.startsWith('cart_timestamp_'));
  
  cartTimestampKeys.forEach(timestampKey => {
    try {
      const timestamp = localStorage.getItem(timestampKey);
      if (!timestamp) return;
      
      const cartAge = Date.now() - parseInt(timestamp);
      const maxAge = CART_LIFESPAN_DAYS * 24 * 60 * 60 * 1000;
      
      if (cartAge > maxAge) {
        const userId = timestampKey.replace('cart_timestamp_', '');
        clearUserCart(userId);
      }
    } catch (error) {
      console.error('Error during cart cleanup:', error);
    }
  });
};

const loadCartFromStorage = (userId) => {
  if (!userId) return [];

  const cartKey = getCartKey(userId);
  const timestampKey = getCartTimestampKey(userId);

  try {
    const cartData = localStorage.getItem(cartKey);
    const timestamp = localStorage.getItem(timestampKey);

    if (!cartData || !timestamp) return [];

    // Check if cart has expired
    const cartAge = Date.now() - parseInt(timestamp);
    const maxAge = CART_LIFESPAN_DAYS * 24 * 60 * 60 * 1000; // Convert days to milliseconds

    if (cartAge > maxAge) {
      // Cart has expired, clean it up
      clearUserCart(userId);
      return [];
    }

    return JSON.parse(cartData);
  } catch (error) {
    console.error('Error loading cart from storage:', error);
    return [];
  }
};

const clearUserCart = (userId) => {
  if (!userId) return;

  const cartKey = getCartKey(userId);
  const timestampKey = getCartTimestampKey(userId);

  localStorage.removeItem(cartKey);
  localStorage.removeItem(timestampKey);
};

const updateCartTimestamp = (userId) => {
  if (!userId) return;

  const timestampKey = getCartTimestampKey(userId);
  localStorage.setItem(timestampKey, Date.now().toString());
};


const cartSlice = createSlice({
  name: "cart",
  initialState: {
    cartItem: [],
    currentUserId: null,
    cartInitialized: false,
  },
  reducers: {
    initializeUserCart: (state, action) => {
      const { userId } = action.payload;
      state.currentUserId = userId;

      if (userId) {
        state.cartItem = loadCartFromStorage(userId);
        updateCartTimestamp(userId);
      } else {
        // If no userId, ensure the cart is empty
        state.cartItem = [];
      }
      state.cartInitialized = true;
    },

    clearCurrentUser: (state) => {
      if (state.currentUserId) {
        saveCartToStorage(state.currentUserId, state.cartItem);
      }
      state.currentUserId = null;
      state.cartItem = [];
      state.cartInitialized = false;
    },

    addToCart: (state, action) => {
      if (!state.currentUserId || !state.cartInitialized) {
        console.error('Cannot add to cart');
        return;
      }

      const chefIndex = state.cartItem.findIndex(
        (chef) =>
          chef.id === action.payload.user_id &&
          chef.delivery_date === action.payload.chef.delivery_date &&
          chef.delivery_slot === action.payload.chef.delivery_slot
      );

      if (chefIndex !== -1) {
        const menuIndex = state.cartItem[chefIndex].menu.findIndex(
          (menu) => menu.id === action.payload.id
        );

        if (menuIndex !== -1) {
          state.cartItem[chefIndex].menu[menuIndex].quantity = action.payload.quantity;
        } else {
          state.cartItem[chefIndex].menu.push(action.payload);
        }
      } else {
        const { chef, ...payload } = action.payload;
        const newChef = {
          ...chef,
          menu: [payload],
        };
        state.cartItem.push(newChef);
      }

      saveCartToStorage(state.currentUserId, state.cartItem);
    },

    removeFromCart: (state, action) => {
        if (!state.currentUserId) return;
        const { chefIndex, menuIndex } = action.payload;

        if (chefIndex >= 0 && menuIndex >= 0 && state.cartItem[chefIndex]) {
            const updatedMenu = state.cartItem[chefIndex].menu.filter(
                (_, index) => index !== menuIndex
            );

            if (updatedMenu.length === 0) {
                // If the menu is empty, remove the entire chef entry
                state.cartItem.splice(chefIndex, 1);
            } else {
                state.cartItem[chefIndex].menu = updatedMenu;
            }

            saveCartToStorage(state.currentUserId, state.cartItem);
        }
    },
    
    updateCartItem: (state, action) => {
      if (!state.currentUserId) return;

      const { chefIndex, menuIndex, key, value } = action.payload;

      state.cartItem = state.cartItem.map((chef, cIdx) => {
        if (cIdx !== chefIndex) return chef;

        return {
          ...chef,
          menu: chef.menu.map((menu, mIdx) => {
            if (mIdx !== menuIndex) return menu;

            return {
              ...menu,
              [key]: value,
            };
          }),
        };
      });

      saveCartToStorage(state.currentUserId, state.cartItem);
    },


    emptyCart: (state) => {
      if (!state.currentUserId) return;
      state.cartItem = [];
      clearUserCart(state.currentUserId);
    },

    cleanCartsReducer: (state) => {
      cleanupExpiredCarts();
    },
    
    onOrderSubmit: (state, action) => {
        if (!state.currentUserId) return;
        const { chefId, delivery_date, delivery_slot } = action.payload;
        
        state.cartItem = state.cartItem.filter(chef => 
            !(chef.id === chefId && chef.delivery_date === delivery_date && chef.delivery_slot === delivery_slot)
        );
        
        saveCartToStorage(state.currentUserId, state.cartItem);
    },
  },
});

export const {
  initializeUserCart,
  clearCurrentUser,
  addToCart,
  removeFromCart,
  updateCartItem,
  cleanCartsReducer,
  emptyCart,
  onOrderSubmit,
} = cartSlice.actions;

export default cartSlice.reducer;